import { Fragment } from "react";
import clsx from "clsx";
import { Listbox, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown } from "@fortawesome/free-solid-svg-icons";

export type SelectOption = { label: string; value: string; dot?: string; count?: number };

type Props = {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  className?: string;
};

export default function Select({ label, value, options, onChange, className }: Props) {
  const selected = options.find((option) => option.value === value) ?? options[0];
  const active = value !== options[0]?.value;

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className={clsx("relative", className)}>
          <Listbox.Button
            aria-label={label}
            className={clsx(
              "flex h-10 w-full items-center justify-between gap-2 rounded-xl border px-3.5 font-[Gilroy-Medium] text-[13px] text-heading-color transition-all duration-200",
              open ? "border-accent ring-4 ring-accent/10" : active ? "border-accent/40 bg-active-background" : "border-edge bg-surface hover:border-edge-strong",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="text-label-muted">{label}</span>
              {selected?.dot ? <span className={clsx("h-2 w-2 rounded-full", selected.dot)} /> : null}
              <span className="truncate">{selected?.label}</span>
            </span>
            <FontAwesomeIcon icon={faChevronDown} className={clsx("text-[10px] text-label-muted transition-transform duration-300", open && "rotate-180 text-accent")} />
          </Listbox.Button>
          <Transition
            as={Fragment}
            enter="transition ease-[cubic-bezier(0.2,0.8,0.2,1)] duration-200"
            enterFrom="opacity-0 scale-95 -translate-y-1"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Listbox.Options className="absolute left-0 z-40 mt-2 max-h-72 min-w-full origin-top-left overflow-auto rounded-xl border border-edge bg-surface-elevated p-1.5 shadow-overlay focus:outline-none">
              {options.map((option) => (
                <Listbox.Option
                  key={option.value || option.label}
                  value={option.value}
                  className={({ active: hovered }) =>
                    clsx("flex cursor-pointer items-center justify-between gap-4 whitespace-nowrap rounded-lg px-3 py-2 font-[Gilroy-Medium] text-[13px] transition-colors", hovered ? "bg-overlay-mid text-heading-color" : "text-light-gray")
                  }
                >
                  {({ selected: isSelected }) => (
                    <>
                      <span className="flex items-center gap-2">
                        {option.dot ? <span className={clsx("h-2 w-2 rounded-full", option.dot)} /> : null}
                        {option.label}
                        {option.count != null ? <span className="rounded-full bg-overlay-mid px-1.5 text-[11px] text-label-muted">{option.count}</span> : null}
                      </span>
                      {isSelected ? <FontAwesomeIcon icon={faCheck} className="text-[10px] text-accent" /> : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
